import React, { useEffect, useRef, useState } from 'react';
import MemberDashboardHistoryList from '../components/MemberDashboardHistoryList';
import {
  fetchDashboardHistoryCredits,
  fetchDashboardHistoryEvents,
  fetchDashboardHistoryLoot,
} from '../dashboardHistoryApi';
import {
  dashboardCardCacheKey,
  getDashboardCardBoot,
  setDashboardCardCache,
} from '../dashboardSessionCache';

const CARD_ID = 'history';

export default function HistoryCard({
  handleId,
  player,
  loading: profileLoading,
  viewerCacheKey = '',
}) {
  const hid = Number(handleId);
  const hidOk = Number.isInteger(hid) && hid >= 1;
  const cacheKey = hidOk ? dashboardCardCacheKey(viewerCacheKey, hid, CARD_ID) : null;
  const sessionBoot = cacheKey ? getDashboardCardBoot(cacheKey) : null;

  const [events, setEvents] = useState(() => sessionBoot?.events ?? []);
  const [creditEntries, setCreditEntries] = useState(() => sessionBoot?.creditEntries ?? []);
  const [lootEntries, setLootEntries] = useState(() => sessionBoot?.lootEntries ?? []);
  const [loading, setLoading] = useState(() => hidOk && !sessionBoot);
  const [error, setError] = useState(sessionBoot?.error ?? null);
  const hasLoadedOnceRef = useRef(!!sessionBoot);

  useEffect(() => {
    if (!hidOk || profileLoading) {
      return undefined;
    }

    let cancelled = false;

    async function load() {
      const hasCache = !!getDashboardCardBoot(cacheKey);
      const isBlankVisit = !hasLoadedOnceRef.current && !hasCache;

      if (isBlankVisit) {
        setLoading(true);
      }
      if (!isBlankVisit) {
        setError(null);
      }

      try {
        const [eventsData, creditsData, lootData] = await Promise.all([
          fetchDashboardHistoryEvents(hid, { scope: 'cluster', filter: 'total' }),
          fetchDashboardHistoryCredits(hid, { filter: 'total' }),
          fetchDashboardHistoryLoot(hid, { filter: 'total' }),
        ]);

        if (cancelled) return;

        if (!eventsData?.ok) {
          setEvents([]);
          setCreditEntries([]);
          setLootEntries([]);
          setError(eventsData?.error || 'Could not load event history.');
          return;
        }

        const nextEvents = Array.isArray(eventsData.events) ? eventsData.events : [];
        const nextCredits = Array.isArray(creditsData?.entries) ? creditsData.entries : [];
        const nextLoot = Array.isArray(lootData?.entries) ? lootData.entries : [];

        setEvents(nextEvents);
        setCreditEntries(nextCredits);
        setLootEntries(nextLoot);
        hasLoadedOnceRef.current = true;
        setDashboardCardCache(cacheKey, {
          events: nextEvents,
          creditEntries: nextCredits,
          lootEntries: nextLoot,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setEvents([]);
        setCreditEntries([]);
        setLootEntries([]);
        setError(e?.message || 'Could not load event history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, hid, hidOk, profileLoading]);

  if (!hidOk) return null;

  const busy =
    (profileLoading && !player) ||
    (loading && !events.length && !creditEntries.length && !lootEntries.length);

  return (
    <section
      className="member-dashboard__panel-card member-dashboard__history-card"
      aria-busy={busy ? 'true' : undefined}
      aria-label="Event history"
      data-resource="private.navbar.personalhub.history"
      data-action="read"
      data-description="Personal hub — event history"
    >
      <div
        className="display-none"
        data-resource="private.navbar.personalhub.history"
        data-action="read"
        data-route="/api/user/private-dashboard/history-events"
        data-description="Personal hub — event history API"
      />
      <div
        className="display-none"
        data-resource="private.navbar.personalhub.history"
        data-action="read"
        data-route="/api/user/private-dashboard/history-credits"
        data-description="Personal hub — event credits API"
      />
      <div
        className="display-none"
        data-resource="private.navbar.personalhub.history"
        data-action="read"
        data-route="/api/user/private-dashboard/history-loot"
        data-description="Personal hub — event loot API"
      />
      <div
        className="display-none"
        data-resource="private.navbar.roster.file.history.bypass"
        data-action="read"
        data-description="Personal hub — event history bypass redaction"
      />

      <div className="member-dashboard__panel-card-head">
        <h2 className="member-dashboard__panel-title">History</h2>
      </div>

      <div className="member-dashboard__panel-card-body">
        {busy ? (
          <p className="member-dashboard__panel-placeholder">Loading…</p>
        ) : error ? (
          <p className="member-dashboard__panel-placeholder">{error}</p>
        ) : (
          <MemberDashboardHistoryList
            player={player}
            events={events}
            creditEntries={creditEntries}
            lootEntries={lootEntries}
          />
        )}
      </div>
    </section>
  );
}
